import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAdmin } from "../../App";
import {
  LayoutDashboard,
  Building2,
  MessageSquare,
  LogOut,
  ShieldCheck,
  Phone,
  Mail,
  Calendar,
  Clock,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  XCircle,
  User,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";

const AdminInquiries = () => {
  const { admin, adminLogout, adminToken } = useAdmin();
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(0);
  const limit = 15;

  useEffect(() => {
    fetchInquiries();
  }, [adminToken, page, status]);

  const fetchInquiries = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status && status !== "all") params.append("status", status);
      params.append("limit", limit);
      params.append("skip", page * limit);

      const response = await axios.get(`${API}/admin/inquiries?${params.toString()}`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      setInquiries(response.data.inquiries);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching inquiries:", error);
      toast.error("Failed to fetch inquiries");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (inquiryId, newStatus) => {
    try {
      await axios.put(
        `${API}/admin/inquiries/${inquiryId}/status?status=${newStatus}`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success("Status updated successfully");
      fetchInquiries();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleLogout = () => {
    adminLogout();
    navigate("/admin");
  };

  const sidebarItems = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/admin/dashboard" },
    { icon: Building2, label: "Properties", path: "/admin/properties" },
    { icon: MessageSquare, label: "Inquiries", path: "/admin/inquiries", active: true },
  ];

  const statusOptions = [
    { value: "pending", label: "Pending", color: "text-amber-400 bg-amber-900/30" },
    { value: "contacted", label: "Contacted", color: "text-blue-400 bg-blue-900/30" },
    { value: "completed", label: "Completed", color: "text-green-400 bg-green-900/30" },
    { value: "cancelled", label: "Cancelled", color: "text-red-400 bg-red-900/30" },
  ];

  const getStatusColor = (s) => {
    return statusOptions.find((opt) => opt.value === s)?.color || "text-stone-400 bg-stone-700";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="min-h-screen bg-stone-900 flex" data-testid="admin-inquiries">
      {/* Sidebar */}
      <aside className="w-64 bg-stone-800 border-r border-stone-700 flex flex-col">
        <div className="p-6 border-b border-stone-700">
          <Link to="/admin/dashboard" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-amber-500 rounded-sm flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <div>
              <div className="font-display text-lg text-white">RERA Admin</div>
              <div className="text-xs text-stone-400">Control Panel</div>
            </div>
          </Link>
        </div>

        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            {sidebarItems.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
                    item.active
                      ? "bg-amber-500/10 text-amber-500 border border-amber-500/20"
                      : "text-stone-400 hover:bg-stone-700 hover:text-white"
                  }`}
                >
                  <item.icon className="w-5 h-5" strokeWidth={1.5} />
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="p-4 border-t border-stone-700">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center text-white font-medium">
              {admin?.name?.charAt(0) || "A"}
            </div>
            <div>
              <p className="text-white text-sm font-medium">{admin?.name}</p>
              <p className="text-stone-400 text-xs">{admin?.email}</p>
            </div>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full border-stone-600 text-stone-400 hover:bg-stone-700 hover:text-white"
          >
            <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <header className="bg-stone-800 border-b border-stone-700 px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-display text-2xl text-white">Inquiries</h1>
              <p className="text-stone-400 text-sm">{total} inquiries received</p>
            </div>
            <Select value={status} onValueChange={(v) => { setStatus(v); setPage(0); }}>
              <SelectTrigger className="w-48 bg-stone-700 border-stone-600 text-white">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Inquiries</SelectItem>
                {statusOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </header>

        <div className="p-8">
          {loading ? (
            <div className="space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="bg-stone-800 border border-stone-700 p-6 rounded-sm animate-pulse">
                  <div className="h-6 bg-stone-700 rounded w-1/3 mb-4" />
                  <div className="h-4 bg-stone-700 rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : inquiries.length === 0 ? (
            <div className="bg-stone-800 border border-stone-700 p-12 rounded-sm text-center">
              <MessageSquare className="w-12 h-12 text-stone-600 mx-auto mb-4" strokeWidth={1.5} />
              <h3 className="font-display text-lg text-white mb-2">No Inquiries Found</h3>
              <p className="text-stone-400">
                {status !== "all" ? "No inquiries match the selected filter." : "No inquiries have been received yet."}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {inquiries.map((inquiry) => (
                <div
                  key={inquiry.inquiry_id}
                  className="bg-stone-800 border border-stone-700 p-6 rounded-sm"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-medium text-white mb-1">{inquiry.property_name}</h3>
                      <p className="text-stone-400 text-sm capitalize">
                        {inquiry.inquiry_type?.replace("_", " ")} Request
                      </p>
                    </div>
                    <span className={`px-3 py-1 text-xs uppercase rounded ${getStatusColor(inquiry.status)}`}>
                      {inquiry.status}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div className="flex items-center gap-2 text-stone-300">
                      <User className="w-4 h-4 text-stone-500" strokeWidth={1.5} />
                      <span>{inquiry.name}</span>
                    </div>
                    <div className="flex items-center gap-2 text-stone-300">
                      <Mail className="w-4 h-4 text-stone-500" strokeWidth={1.5} />
                      <a href={`mailto:${inquiry.email}`} className="hover:text-amber-400">
                        {inquiry.email}
                      </a>
                    </div>
                    <div className="flex items-center gap-2 text-stone-300">
                      <Phone className="w-4 h-4 text-stone-500" strokeWidth={1.5} />
                      <a href={`tel:${inquiry.phone}`} className="hover:text-amber-400">
                        {inquiry.phone}
                      </a>
                    </div>
                  </div>

                  {(inquiry.preferred_date || inquiry.preferred_time) && (
                    <div className="flex items-center gap-4 mb-4 text-stone-400 text-sm">
                      {inquiry.preferred_date && (
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" strokeWidth={1.5} />
                          {inquiry.preferred_date}
                        </div>
                      )}
                      {inquiry.preferred_time && (
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" strokeWidth={1.5} />
                          {inquiry.preferred_time}
                        </div>
                      )}
                    </div>
                  )}

                  {inquiry.message && (
                    <div className="bg-stone-900 p-3 rounded text-stone-300 text-sm mb-4">
                      {inquiry.message}
                    </div>
                  )}

                  <div className="flex items-center justify-between border-t border-stone-700 pt-4">
                    <p className="text-stone-500 text-xs">
                      Received: {formatDate(inquiry.created_at)}
                    </p>
                    <div className="flex items-center gap-2">
                      <span className="text-stone-400 text-sm">Update Status:</span>
                      <div className="flex gap-1">
                        {inquiry.status !== "contacted" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateStatus(inquiry.inquiry_id, "contacted")}
                            className="border-blue-600 text-blue-400 hover:bg-blue-900/30 h-8 px-3 text-xs"
                          >
                            Contacted
                          </Button>
                        )}
                        {inquiry.status !== "completed" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateStatus(inquiry.inquiry_id, "completed")}
                            className="border-green-600 text-green-400 hover:bg-green-900/30 h-8 px-3 text-xs"
                          >
                            <CheckCircle className="w-3 h-3 mr-1" strokeWidth={1.5} />
                            Complete
                          </Button>
                        )}
                        {inquiry.status !== "cancelled" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateStatus(inquiry.inquiry_id, "cancelled")}
                            className="border-red-600 text-red-400 hover:bg-red-900/30 h-8 px-3 text-xs"
                          >
                            <XCircle className="w-3 h-3 mr-1" strokeWidth={1.5} />
                            Cancel
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between bg-stone-800 border border-stone-700 px-6 py-4 rounded-sm">
                  <p className="text-stone-400 text-sm">
                    Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(Math.max(0, page - 1))}
                      disabled={page === 0}
                      className="border-stone-600 text-stone-400 hover:bg-stone-700"
                    >
                      <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                    <span className="text-stone-400 text-sm">
                      Page {page + 1} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                      disabled={page >= totalPages - 1}
                      className="border-stone-600 text-stone-400 hover:bg-stone-700"
                    >
                      <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminInquiries;
